import { Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom, of, Subject, EMPTY } from 'rxjs';
import { catchError, switchMap, takeUntil, timeout } from 'rxjs/operators';
import {
  ApiDevisGuarantee,
  ApiDevisGroups,
  ApiDevisProject,
  ApiPolicy,
  ApiPolicyAction,
  ApiPolicyDetail,
  ApiPolicyDetailItem,
  ApiPolicyDetailSection,
  ApiPolicyRisk,
  ApiPolicySuperAction,
  ApiSinister,
  ApiSinisterEvent,
  ApiSinisterGroups,
} from '../../../../core/models/myspace-api.models';
import { ApiNestedProduct, ApiProductCatalogItem } from '../../../../core/models/product-api.models';
import { PolicyClaimConcern } from './policy-claim-form/policy-claim-form.component';
import { AuthService } from '../../../../core/services/auth.service';
import { InfoApiService } from '../../../../core/services/info-api.service';
import { MyspaceApiService } from '../../../../core/services/myspace-api.service';
import { ProductApiService } from '../../../../core/services/product-api.service';
import { SouscriptionApiService } from '../../../../core/services/souscription-api.service';
import { TokenStorageService } from '../../../../core/services/token-storage.service';
import { extractApiErrorMessage } from '../../../../core/utils/api-error.util';
import { extractPdfBase64, openPdfFromBase64 } from '../../../../core/utils/pdf.util';
import { PHONE_INPUT_DEFAULT_DIAL_CODE } from '../../../../shared/config/phone-input.config';
import { toNationalPhone, toPolicyPhoneNumber } from '../../../../shared/utils/phone.utils';

type MyspaceTab = 'polices' | 'devis' | 'prestations';

interface MyspaceTabItem {
  id: MyspaceTab;
  label: string;
  icon: string;
}

interface PolicyGroupView {
  name: string;
  items: ApiPolicy[];
}

interface DevisGroupView {
  name: string;
  items: ApiDevisProject[];
}

interface SinisterGroupView {
  name: string;
  items: ApiSinister[];
}

interface DetailRow {
  label: string;
  value: string;
}

interface PersonCard {
  title: string;
  rows: DetailRow[];
}

interface AccordionSectionView {
  title: string;
  kind: 'rows' | 'people' | 'guarantees' | 'events';
  rows?: DetailRow[];
  people?: PersonCard[];
  guarantees?: string[];
  events?: DetailRow[];
}

type PolicyPayKind = 'free' | 'periodic' | 'quittance';

interface PolicyPaymentConfig {
  isFreePay: boolean;
  isPrevoyance: boolean;
  kind: PolicyPayKind;
  title: string;
  description: string;
}

@Component({
  selector: 'app-my-space',
  templateUrl: './my-space.component.html',
  styleUrls: ['./my-space.component.scss'],
})
export class MySpaceComponent implements OnInit, OnDestroy {
  readonly tabs: MyspaceTabItem[] = [
    { id: 'polices', label: 'Polices', icon: 'verified_user' },
    { id: 'devis', label: 'Devis', icon: 'description' },
    { id: 'prestations', label: 'Prestations', icon: 'medical_services' },
  ];

  activeTab: MyspaceTab = 'polices';
  currency = 'FCFA';

  policies: ApiPolicy[] = [];
  devisGroups: DevisGroupView[] = [];
  sinisterGroups: SinisterGroupView[] = [];

  policiesLoading = true;
  devisLoading = true;
  sinistersLoading = true;
  policiesError = '';
  devisError = '';
  sinistersError = '';
  toastMessage = '';

  selectedPolicy: ApiPolicy | null = null;
  selectedDevis: ApiDevisProject | null = null;
  selectedSinister: ApiSinister | null = null;
  deletingDevis = false;
  errorModalMessage: string | null = null;
  errorModalCanRetry = false;

  policyDetail: ApiPolicyDetail | null = null;
  policyDetailLoading = false;
  policyDetailError = '';
  policyAccordionSections: AccordionSectionView[] = [];
  devisAccordionSections: AccordionSectionView[] = [];
  sinisterAccordionSections: AccordionSectionView[] = [];
  cpDownloading = false;
  atDownloading = false;
  showDevisPayment = false;
  devisProduct: ApiNestedProduct | null = null;
  validatingDevisPayment = false;
  policyProduct: ApiNestedProduct | null = null;
  openSuperActionId: string | null = null;
  showPolicyPaymentForm = false;
  showPolicyPaymentWidget = false;
  showPolicyClaimChooser = false;
  showPolicyClaimForm = false;
  policyPayment: PolicyPaymentConfig | null = null;
  policyPayFreeAmount = '0';
  policyPayPeriods = '';
  policyPayTotalLabel = '0';
  policyPayAmount = 0;
  policyPayInteracted = false;
  policyPayValidating = false;
  validatingPolicyPayment = false;
  selectedClaimAction: ApiPolicyAction | null = null;
  selectedClaimConcern: PolicyClaimConcern | null = null;

  private phoneNumber = '';
  private policyPhoneNumber = '';
  private userId = '';
  private lastLoadKey = '';
  private errorModalRetry: (() => void) | null = null;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly policyDetailLoad$ = new Subject<ApiPolicy | null>();

  constructor(
    private readonly authService: AuthService,
    private readonly tokenStorage: TokenStorageService,
    private readonly myspaceApi: MyspaceApiService,
    private readonly infoApi: InfoApiService,
    private readonly souscriptionApi: SouscriptionApiService,
    private readonly productApi: ProductApiService,
  ) {}

  get policyProductCount(): number {
    return new Set(this.policies.map((item) => this.policyCategoryKey(item)).filter(Boolean)).size;
  }

  /** Groupes produit comme le mobile (`idencate` + en-tête `productName`). */
  get policyGroups(): PolicyGroupView[] {
    const groups: PolicyGroupView[] = [];
    let previousKey = '';

    for (const policy of this.policies) {
      const key = this.policyCategoryKey(policy) || this.policyProductName(policy);
      const name = this.policyProductName(policy);
      if (!groups.length || key.toLowerCase() !== previousKey.toLowerCase()) {
        groups.push({ name, items: [policy] });
        previousKey = key;
      } else {
        groups[groups.length - 1].items.push(policy);
      }
    }

    return groups;
  }

  get devisCount(): number {
    return this.devisGroups.reduce((sum, group) => sum + group.items.length, 0);
  }

  get sinisterInProgressCount(): number {
    return this.flatSinisters.filter((item) => ['0', '1'].includes(String(item.status ?? ''))).length;
  }

  get sinisterClosedCount(): number {
    return this.flatSinisters.length - this.sinisterInProgressCount;
  }

  get hasSinisters(): boolean {
    return this.flatSinisters.length > 0;
  }

  get showDetail(): boolean {
    return !!(this.selectedPolicy || this.selectedDevis || this.selectedSinister);
  }

  get showPolicyOverview(): boolean {
    return !!(
      this.selectedPolicy &&
      !this.showPolicyPaymentForm &&
      !this.showPolicyPaymentWidget &&
      !this.showPolicyClaimChooser &&
      !this.showPolicyClaimForm
    );
  }

  get policySuperActions(): ApiPolicySuperAction[] {
    return this.policyDetail?.superActions || [];
  }

  get policyClaimRisks(): ApiPolicyRisk[] {
    return this.policyDetail?.riskbs || [];
  }

  get policyPeriodsLabel(): string {
    const period = String(
      this.pick(this.policyDetail?.quittanceDetail, 'libeperi', 'LIBEPERI') || '',
    );
    return /annuel/i.test(period) ? "Nombre d'années couvertes" : 'Nombre de mois couverts';
  }

  get policyPayFreeError(): string {
    if (!this.policyPayInteracted) {
      return '';
    }
    const raw = this.policyPayFreeAmount.replace(/\s/g, '');
    if (!raw) {
      return 'Veuillez renseigner le montant à payer';
    }
    const amount = this.parsePayAmount(this.policyPayFreeAmount);
    if (amount === null) {
      return 'Valeur numérique invalide';
    }
    if (amount <= 0) {
      return `Minimum 1 ${this.currency}`;
    }
    return '';
  }

  get policyPayPeriodsError(): string {
    if (!this.policyPayInteracted) {
      return '';
    }
    if (!this.policyPayPeriods) {
      return 'Veuillez saisir le nombre de périodes';
    }
    const periods = Number.parseInt(this.policyPayPeriods, 10);
    if (!Number.isFinite(periods) || periods <= 0) {
      return 'Nombre de périodes invalide';
    }
    return '';
  }

  get policyPayPolicyNumber(): string {
    return this.policyValue(this.selectedPolicy || {}, 'NUMEPOLI', 'numpoli', 'numepoli');
  }

  get claimPolicyNumber(): string {
    return this.policyValue(this.selectedPolicy || {}, 'NUMEPOLI', 'numpoli', 'numepoli');
  }

  get claimTypeCode(): string {
    return (
      this.policyValue(this.selectedPolicy || {}, 'CODECATE', 'codecate') ||
      this.policyValue(this.selectedPolicy || {}, 'IDENCATE', 'idencate')
    );
  }

  get claimUserId(): string {
    return this.userId;
  }

  get detailTitle(): string {
    if (this.selectedPolicy) {
      if (this.showPolicyPaymentForm || this.showPolicyPaymentWidget) {
        return this.policyPayment?.title || 'Paiement';
      }
      if (this.showPolicyClaimChooser || this.showPolicyClaimForm) {
        return this.sentenceCase(this.selectedClaimAction?.label || 'Prestation');
      }
      return 'Détails de la police';
    }
    if (this.selectedDevis) {
      return String(this.selectedDevis['pageTitle'] || this.selectedDevis.productName || 'Détails du devis');
    }
    if (this.selectedSinister) {
      const name = this.sentenceCase(this.selectedSinister.claimNaming || '');
      return name ? `Détails ${name}` : 'Détails du sinistre';
    }
    return '';
  }

  get canDownloadPolicyCp(): boolean {
    return Boolean(this.policyCpIds.idenetat && this.policyCpIds.codeinte && this.policyCpIds.numepoli);
  }

  get canDownloadSinisterAt(): boolean {
    return Boolean(this.userId && this.selectedSinister?.policeNumber && this.selectedSinister?.reference);
  }

  get devisPayAmount(): number {
    return this.selectedDevis ? this.devisAmountValue(this.selectedDevis) ?? 0 : 0;
  }

  get devisPayLabel(): string {
    return String(this.selectedDevis?.['actionButtonLabel'] || 'Envoyer');
  }

  get devisPolicyNumber(): string {
    const quittance = this.selectedDevis?.quittance || {};
    return String(quittance['numepoli'] ?? quittance['NUMEPOLI'] ?? '');
  }

  private get policyCpIds(): { idenetat: string; codeinte: string; numepoli: string } {
    const policy = this.selectedPolicy;
    return {
      idenetat: String(policy?.idenetat || policy?.['idenetat'] || ''),
      codeinte: this.policyValue(policy || {}, 'CODEINTE', 'codeint'),
      numepoli: this.policyValue(policy || {}, 'NUMEPOLI', 'numpoli'),
    };
  }

  get tabIndicatorTransform(): string {
    const index = this.tabs.findIndex((tab) => tab.id === this.activeTab);
    return `translateX(${Math.max(index, 0) * 100}%)`;
  }

  private get flatSinisters(): ApiSinister[] {
    return this.sinisterGroups.flatMap((group) => group.items);
  }

  ngOnInit(): void {
    this.infoApi
      .getCompanyInfo()
      .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe((info) => {
        const currency = (info?.currency || '').trim();
        if (currency) {
          this.currency = currency.toUpperCase();
        }
      });

    this.authService.userProfile$.pipe(takeUntil(this.destroy$)).subscribe((profile) => {
      const rawPhone = (profile?.phone || this.tokenStorage.getUsername() || '').trim();
      const dialCode = (profile?.codePays || this.tokenStorage.getDialCode() || PHONE_INPUT_DEFAULT_DIAL_CODE).trim();
      this.phoneNumber = toNationalPhone(rawPhone, dialCode);
      this.policyPhoneNumber = toPolicyPhoneNumber(rawPhone, dialCode);
      this.userId = profile?.id?.trim() || this.authService.getUserId() || '';

      const loadKey = `${this.userId}|${this.phoneNumber}|${this.policyPhoneNumber}`;
      if (!this.phoneNumber && !this.userId) {
        return;
      }
      if (loadKey === this.lastLoadKey) {
        return;
      }
      this.lastLoadKey = loadKey;
      this.loadAll();
    });

    this.policyDetailLoad$
      .pipe(
        switchMap((policy) => {
          if (!policy) {
            return EMPTY;
          }
          return this.myspaceApi.getPolicyDetail(this.toPolicyDetailDto(policy)).pipe(
            timeout(20000),
            catchError((err) => {
              this.policyDetailLoading = false;
              this.policyDetailError = extractApiErrorMessage(
                err,
                'Impossible de charger le détail de la police.',
              );
              return of(null);
            }),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((detail) => {
        if (!this.selectedPolicy) {
          return;
        }
        this.policyDetailLoading = false;
        if (!detail) {
          return;
        }
        this.policyDetail = detail;
        this.policyAccordionSections = this.buildPolicySections(this.selectedPolicy, detail);
      });
  }

  ngOnDestroy(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.policyDetailLoad$.next(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectTab(tab: MyspaceTab): void {
    this.activeTab = tab;
    this.closeDetail();
  }

  reload(): void {
    this.lastLoadKey = '';
    this.loadAll();
  }

  closeErrorModal(): void {
    this.errorModalMessage = null;
    this.errorModalCanRetry = false;
    this.errorModalRetry = null;
  }

  retryErrorModal(): void {
    const retry = this.errorModalRetry;
    this.closeErrorModal();
    retry?.();
  }

  openPolicy(policy: ApiPolicy): void {
    this.selectedPolicy = policy;
    this.selectedDevis = null;
    this.selectedSinister = null;
    this.resetDetailExtras();
    this.policyAccordionSections = [];
    this.policyDetailLoading = true;
    this.policyDetailError = '';
    this.policyDetailLoad$.next(policy);
    this.resolvePolicyProduct(policy);
  }

  retryPolicyDetail(): void {
    if (!this.selectedPolicy) {
      return;
    }
    this.policyDetailError = '';
    this.policyDetailLoading = true;
    this.policyDetailLoad$.next(this.selectedPolicy);
  }

  openDevis(devis: ApiDevisProject): void {
    this.selectedDevis = devis;
    this.selectedPolicy = null;
    this.selectedSinister = null;
    this.resetDetailExtras();
    this.devisAccordionSections = this.buildDevisSections(devis);
    this.resolveDevisProduct(devis);
  }

  openSinister(sinister: ApiSinister): void {
    this.selectedSinister = sinister;
    this.selectedPolicy = null;
    this.selectedDevis = null;
    this.resetDetailExtras();
    this.sinisterAccordionSections = this.buildSinisterSections(sinister);
  }

  closeDetail(): void {
    this.policyDetailLoad$.next(null);
    this.selectedPolicy = null;
    this.selectedDevis = null;
    this.selectedSinister = null;
    this.resetDetailExtras();
  }

  onDetailBack(): void {
    if (this.showPolicyPaymentWidget) {
      this.showPolicyPaymentWidget = false;
      this.showPolicyPaymentForm = true;
      return;
    }
    if (this.showPolicyClaimForm) {
      this.showPolicyClaimForm = false;
      this.selectedClaimConcern = null;
      this.showPolicyClaimChooser = true;
      return;
    }
    if (this.showPolicyPaymentForm || this.showPolicyClaimChooser) {
      this.resetPolicyActionView();
      return;
    }
    this.closeDetail();
  }

  toggleSuperAction(superAction: ApiPolicySuperAction, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const id = String(superAction.id || superAction.key || superAction.label || '');
    this.openSuperActionId = this.openSuperActionId === id ? null : id;
  }

  closeSuperActionMenu(): void {
    this.openSuperActionId = null;
  }

  superActionId(superAction: ApiPolicySuperAction): string {
    return String(superAction.id || superAction.key || superAction.label || '');
  }

  superActionLabel(superAction: ApiPolicySuperAction): string {
    const label = String(superAction.label || superAction.name || '').trim();
    if (label.length <= 13) {
      return label;
    }
    return `${label.slice(0, 12)}...`;
  }

  tintedIconStyle(url: string | null | undefined): Record<string, string> {
    if (!url) {
      return {};
    }
    const image = `url("${String(url).replace(/"/g, '%22')}")`;
    return {
      'mask-image': image,
      '-webkit-mask-image': image,
    };
  }

  superActionFallbackIcon(superAction: ApiPolicySuperAction): string {
    const key = `${superAction.key || ''} ${superAction.label || ''} ${superAction.name || ''}`.toLowerCase();
    if (key.includes('pai') || key.includes('pay')) {
      return 'payments';
    }
    if (key.includes('prest') || key.includes('sinist')) {
      return 'medical_services';
    }
    return 'more_horiz';
  }

  onPolicyAction(action: ApiPolicyAction, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.closeSuperActionMenu();
    const key = String(action.key || action.name || '').trim();
    const payment = this.policyPaymentConfig(key);
    if (payment) {
      setTimeout(() => this.openPolicyPayment(payment));
      return;
    }
    this.openPolicyClaimChooser(action);
  }

  private policyPaymentConfig(key: string): PolicyPaymentConfig | null {
    switch (key) {
      case 'PAY_COTISATION_LIBRE':
        return {
          isFreePay: true,
          isPrevoyance: false,
          kind: 'free',
          title: 'Cotisation libre',
          description: 'paiement cotisation libre *',
        };
      case 'PAY_COTISATION_UNIQUE':
        return {
          isFreePay: true,
          isPrevoyance: true,
          kind: 'free',
          title: 'Paiement unique',
          description: 'paiement cotisation libre *',
        };
      case 'PAY_COTISATION_PERIODIQUE':
        return {
          isFreePay: false,
          isPrevoyance: false,
          kind: 'periodic',
          title: 'Cotisation périodique',
          description: 'paiement cotisation périodique',
        };
      case 'PAY_COTISATION_TERME':
        return {
          isFreePay: false,
          isPrevoyance: true,
          kind: 'periodic',
          title: 'Paiement périodique',
          description: 'paiement cotisation périodique',
        };
      case 'PAY_QUITTANCE_IMPAYEE':
        return {
          isFreePay: false,
          isPrevoyance: false,
          kind: 'quittance',
          title: 'Quittance impayée',
          description: 'paiement quittance impayée',
        };
      default:
        return null;
    }
  }

  onPolicyPayPeriodsInput(value: string): void {
    this.policyPayPeriods = value.replace(/[^\d]/g, '');
    if (!this.policyPayPeriods) {
      this.policyPayTotalLabel = '0';
      this.policyPayAmount = 0;
      return;
    }
    const periods = Number.parseInt(this.policyPayPeriods, 10) || 0;
    const total = periods * this.policyPrimeValue();
    this.policyPayTotalLabel = this.formatPayAmount(total);
    this.policyPayAmount = total;
  }

  onPolicyPayFreeAmountInput(value: string): void {
    let text = value.replace(/[^0-9.]/g, '');
    const dotIndex = text.indexOf('.');
    if (dotIndex !== -1) {
      text = text.slice(0, dotIndex + 1) + text.slice(dotIndex + 1).replace(/\./g, '');
    }
    if (!text) {
      this.policyPayFreeAmount = '';
      this.policyPayAmount = 0;
      return;
    }
    const parsed = Number(text);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const formatted = this.formatPayAmount(parsed);
    this.policyPayFreeAmount = text.endsWith('.') && !formatted.includes('.') ? `${formatted}.` : formatted;
    this.policyPayAmount = parsed;
  }

  onPolicyPayFieldBlur(): void {
    this.policyPayInteracted = true;
  }

  async validatePolicyPayment(): Promise<void> {
    if (!this.policyPayment || this.policyPayValidating) {
      return;
    }

    this.policyPayInteracted = true;

    if (this.policyPayment.isFreePay) {
      if (this.policyPayFreeError) {
        return;
      }
      this.showPolicyPaymentForm = false;
      this.showPolicyPaymentWidget = true;
      return;
    }

    if (this.policyPayPeriodsError) {
      return;
    }

    this.policyPayAmount = this.parsePayAmount(this.policyPayTotalLabel) ?? this.policyPayAmount;

    if (this.policyPayment.kind === 'quittance') {
      this.showPolicyPaymentForm = false;
      this.showPolicyPaymentWidget = true;
      return;
    }

    const policy = this.selectedPolicy;
    const codeinte = this.policyValue(policy || {}, 'CODEINTE', 'codeint', 'codeinte');
    const numepoli = this.policyValue(policy || {}, 'NUMEPOLI', 'numpoli', 'numepoli');
    if (!codeinte || !numepoli) {
      this.showErrorModal('Numéro de police incomplet pour le paiement.');
      return;
    }

    this.policyPayValidating = true;
    try {
      const totaEnca = await firstValueFrom(
        this.souscriptionApi.consultLifePolicy({
          NUMEPOLI: numepoli,
          CODEINTE: codeinte,
          REFEINTE: '',
        }),
      );
      if (totaEnca === 0) {
        this.resetPolicyActionView();
        this.showErrorModal("Vous n'avez pas d'impayé !");
        return;
      }
      this.showPolicyPaymentForm = false;
      this.showPolicyPaymentWidget = true;
    } catch (err) {
      this.showErrorModal(
        extractApiErrorMessage(err, 'Impossible de vérifier les impayés.'),
        () => {
          void this.validatePolicyPayment();
        },
      );
    } finally {
      this.policyPayValidating = false;
    }
  }

  async onPolicyPaymentCompleted(reference: string): Promise<void> {
    const policy = this.selectedPolicy;
    const payment = this.policyPayment;
    if (!policy || !payment) {
      return;
    }

    if (payment.kind === 'quittance') {
      this.showToast('Paiement effectué avec succès');
      this.closeDetail();
      this.loadAll();
      return;
    }

    const codeinte = this.policyValue(policy, 'CODEINTE', 'codeint', 'codeinte');
    const numepoli = this.policyValue(policy, 'NUMEPOLI', 'numpoli', 'numepoli');
    const phone = this.policyPhoneNumber || this.phoneNumber;
    const dto: Record<string, unknown> = {
      CODEINTE: codeinte,
      NUMEPOLI: numepoli,
      REFEENCA: phone,
      MONTENCA: String(this.policyPayAmount),
      MODEPAIE: 'EM',
      CODEBANQ: 28,
      CODBANSO: 625,
      TELEASSU: phone,
      REFERENCE: reference,
    };

    this.validatingPolicyPayment = true;
    try {
      if (payment.kind === 'free') {
        dto['DATEENCA'] = this.nowLikeDart();
        dto['TYPEVERS'] = '';
        await firstValueFrom(this.souscriptionApi.handleFreePayment(dto));
      } else {
        dto['DAVAOPBA'] = '';
        await firstValueFrom(this.souscriptionApi.handlePeriodicPayment(dto));
      }
      this.showToast('Opération effectuée avec succès !');
      this.closeDetail();
      this.loadAll();
    } catch (err) {
      this.showErrorModal(
        extractApiErrorMessage(err, 'Paiement reçu, mais l’encaissement a échoué.'),
        () => {
          void this.onPolicyPaymentCompleted(reference);
        },
      );
    } finally {
      this.validatingPolicyPayment = false;
    }
  }

  claimRiskTitle(risk: ApiPolicyRisk): string {
    return this.claimConcern(risk).first.trim() || 'Sans nom';
  }

  claimRiskLabel(): string {
    const type = String(this.policyDetail?.sinisterType || '').trim();
    if (type === 'assure') {
      return "Nom de l'assuré";
    }
    if (type === 'vehicle') {
      return 'Immatriculation du véhicule';
    }
    return 'Nom du bénéficiaire';
  }

  claimRiskIcon(): string {
    const type = String(this.policyDetail?.sinisterType || '').trim();
    if (type === 'vehicle') {
      return 'directions_car';
    }
    if (type === 'beneficiaires') {
      return 'groups';
    }
    return 'person_outline';
  }

  selectClaimRisk(risk: ApiPolicyRisk): void {
    this.selectedClaimConcern = this.claimConcern(risk);
    this.showPolicyClaimChooser = false;
    this.showPolicyClaimForm = true;
  }

  onClaimCompleted(): void {
    this.closeDetail();
    this.activeTab = 'prestations';
    this.loadAll();
  }

  onClaimFailed(event: { message: string; retry: () => void }): void {
    this.showErrorModal(event.message, event.retry);
  }

  private claimConcern(risk: ApiPolicyRisk): PolicyClaimConcern {
    const type = String(this.policyDetail?.sinisterType || '').trim();
    const nomMemb = String(this.pick(risk, 'NOM_MEMB', 'nomMemb') || '').trim();

    if (type === 'assure') {
      const first = String(this.pick(risk, 'NOM_BENE') || '').trim();
      const second = String(this.pick(risk, 'PRENASSU') || '').trim();
      const name = `${first} ${second}`.trim() || nomMemb || 'Sans nom';
      return { first: name, second: name, label: "Nom de l'assuré" };
    }
    if (type === 'vehicle') {
      const mark = String(this.pick(risk, 'MARQVEHI') || '').trim();
      const vehicleType = String(this.pick(risk, 'TYPEVEHI') || '').trim();
      const immat = String(this.pick(risk, 'NUMEIMMA') || '').trim();
      const first = `${mark} ${vehicleType}`.trim() || nomMemb || 'Sans nom';
      return {
        first,
        second: immat || first,
        label: 'Immatriculation du véhicule',
      };
    }
    const name = nomMemb || String(this.pick(risk, 'NOM_ASSU') || '').trim() || 'Sans nom';
    return { first: name, second: name, label: 'Nom du bénéficiaire ' };
  }

  private openPolicyPayment(config: PolicyPaymentConfig): void {
    this.resetPolicyActionView();
    this.policyPayment = config;
    this.showPolicyPaymentForm = true;
    this.policyPayFreeAmount = this.formatPayAmount(0);
    this.policyPayPeriods = '';
    this.policyPayTotalLabel = this.formatPayAmount(0);
    this.policyPayAmount = 0;
    this.policyPayInteracted = false;
  }

  private openPolicyClaimChooser(action: ApiPolicyAction): void {
    this.resetPolicyActionView();
    this.selectedClaimAction = action;
    this.showPolicyClaimChooser = true;
  }

  private resetPolicyActionView(): void {
    this.openSuperActionId = null;
    this.showPolicyPaymentForm = false;
    this.showPolicyPaymentWidget = false;
    this.showPolicyClaimChooser = false;
    this.showPolicyClaimForm = false;
    this.policyPayment = null;
    this.policyPayFreeAmount = this.formatPayAmount(0);
    this.policyPayPeriods = '';
    this.policyPayTotalLabel = this.formatPayAmount(0);
    this.policyPayAmount = 0;
    this.policyPayInteracted = false;
    this.policyPayValidating = false;
    this.validatingPolicyPayment = false;
    this.selectedClaimAction = null;
    this.selectedClaimConcern = null;
  }

  private policyPrimeValue(): number {
    const fromDetail = this.pick(this.policyDetail?.quittanceDetail, 'primtota', 'PRIMTOTA', 'primeotta');
    if (fromDetail != null && String(fromDetail).trim() !== '') {
      if (typeof fromDetail === 'number') {
        return Number.isFinite(fromDetail) ? fromDetail : 0;
      }
      return this.parsePayAmount(String(fromDetail)) ?? 0;
    }
    return (
      this.toNumber(
        this.pick(this.selectedPolicy || {}, 'PRIMTOTA', 'total', 'primeNette', 'primeotta', 'PRIMTOTA_LDE'),
      ) ?? 0
    );
  }

  private formatPayAmount(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value).replace(/,/g, ' ');
  }

  private parsePayAmount(value: string): number | null {
    const normalized = value.replace(/\s/g, '').replace(/,/g, '').trim();
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private nowLikeDart(): string {
    const date = new Date();
    const pad = (value: number, width = 2) => String(value).padStart(width, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
      date.getMinutes(),
    )}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 3)}`;
  }

  private resolvePolicyProduct(policy: ApiPolicy): void {
    this.policyProduct = null;
    this.productApi
      .list()
      .pipe(catchError(() => of([] as ApiProductCatalogItem[])), takeUntil(this.destroy$))
      .subscribe((types) => {
        this.policyProduct = this.findPolicyProduct(types, policy);
      });
  }

  private findPolicyProduct(types: ApiProductCatalogItem[], policy: ApiPolicy): ApiNestedProduct | null {
    const cat = this.policyValue(policy, 'IDENCATE', 'idencate', 'orsyIdentCategory', 'orsyidentcategory');
    const code = this.policyValue(policy, 'CODECATE', 'codecate', 'orsysCodeCategory');
    let byBoth: ApiNestedProduct | null = null;
    let byCat: ApiNestedProduct | null = null;
    let byCode: ApiNestedProduct | null = null;

    types.forEach((type) => {
      (type.products || []).forEach((product) => {
        const matchesCat = Boolean(cat) && String(product.orsyIdentCategory || '') === cat;
        const matchesCode =
          Boolean(code) && (product.orsysCodeCategory === code || product.code === code);
        if (matchesCat && matchesCode) {
          byBoth ??= product;
        }
        if (matchesCat) {
          byCat ??= product;
        }
        if (matchesCode) {
          byCode ??= product;
        }
      });
    });
    return byBoth || byCat || byCode;
  }

  downloadPolicyCp(): void {
    if (!this.canDownloadPolicyCp || this.cpDownloading) {
      return;
    }
    const { idenetat, codeinte, numepoli } = this.policyCpIds;
    this.cpDownloading = true;
    this.souscriptionApi.fetchPrint(idenetat, codeinte, numepoli).subscribe({
      next: (base64Data) => {
        try {
          openPdfFromBase64(base64Data, `cp_${numepoli}.pdf`);
        } catch {
          this.showErrorModal('Impossible d’ouvrir le document CP.');
        }
        this.cpDownloading = false;
      },
      error: (err) => {
        this.cpDownloading = false;
        this.showErrorModal(
          extractApiErrorMessage(err, 'Impossible de télécharger les conditions particulières.'),
          () => this.downloadPolicyCp(),
        );
      },
    });
  }

  downloadSinisterAt(): void {
    if (!this.canDownloadSinisterAt || this.atDownloading || !this.selectedSinister) {
      return;
    }
    const policy = String(this.selectedSinister.policeNumber);
    const reference = String(this.selectedSinister.reference);
    this.atDownloading = true;
    this.myspaceApi.downloadSinisterReceipt(this.userId, policy, reference).subscribe({
      next: (raw) => {
        try {
          openPdfFromBase64(extractPdfBase64(raw), `at_${reference}.pdf`);
        } catch {
          this.showErrorModal('Impossible d’ouvrir l’attestation.');
        }
        this.atDownloading = false;
      },
      error: (err) => {
        this.atDownloading = false;
        this.showErrorModal(
          extractApiErrorMessage(err, 'Impossible de télécharger l’attestation.'),
          () => this.downloadSinisterAt(),
        );
      },
    });
  }

  startDevisPayment(): void {
    if (this.devisPayAmount <= 0) {
      this.showErrorModal('Impossible de lancer le paiement pour ce devis.');
      return;
    }
    this.showDevisPayment = true;
  }

  async onDevisPaymentCompleted(reference: string): Promise<void> {
    const devis = this.selectedDevis;
    if (!devis) {
      return;
    }
    this.validatingDevisPayment = true;
    try {
      const quittance = devis.quittance || {};
      const risk = this.asRecord(devis['risk']);
      const payload = this.asRecord(risk['payload']);
      await firstValueFrom(
        this.souscriptionApi.fetchAfnv({
          phoneNumber: String(risk['phone'] || this.phoneNumber),
          intermediateCode: String(quittance['codeInte'] ?? quittance['CODEINTE'] ?? ''),
          policyNumber: this.devisPolicyNumber,
          orsysIdentCategory: String(payload['orsysIdentCategory'] || this.devisProduct?.orsyIdentCategory || ''),
          MONTENCA: this.devisPayAmount,
          reference,
        }),
      );
      this.showToast('Devis payé avec succès');
      this.closeDetail();
      this.loadAll();
    } catch (err) {
      this.showErrorModal(
        extractApiErrorMessage(err, 'Paiement reçu, mais la validation du devis a échoué.'),
        () => {
          void this.onDevisPaymentCompleted(reference);
        },
      );
    } finally {
      this.validatingDevisPayment = false;
    }
  }

  contractNumber(policy: ApiPolicy): string {
    return `${this.policyValue(policy, 'CODEINTE', 'codeint', 'codeinte') || '~'}-${this.policyValue(policy, 'NUMEPOLI', 'numpoli', 'numepoli') || '~'}-${this.policyValue(policy, 'NUMEAVEN', 'numeave', 'numaven') || '~'}`;
  }

  policyStatus(policy: ApiPolicy): string {
    return this.policyValue(policy, 'ETATPOLI', 'etatPolice', 'etatpoli') || 'Active';
  }

  policyProductName(policy: ApiPolicy): string {
    return this.policyValue(policy, 'productName', 'product_name') || 'Police assurance';
  }

  policyImage(policy: ApiPolicy): string {
    return this.policyValue(policy, 'policyUrlImage', 'policy_url_image');
  }

  policyEffectDate(policy: ApiPolicy): string {
    return this.displayApiDate(this.policyValue(policy, 'DATEEFFE', 'effectDate', 'dateeffe', 'dateEffe'));
  }

  policyEndDate(policy: ApiPolicy): string {
    return this.displayApiDate(this.policyValue(policy, 'DATEECHE', 'issueDate', 'dateeche', 'dateEche'));
  }

  policyAssured(policy: ApiPolicy): string {
    return this.policyValue(policy, 'ASSURE', 'assure') || '-';
  }

  policyPrime(policy: ApiPolicy): string {
    const fromDetail = this.pick(this.policyDetail?.quittanceDetail, 'primtota', 'PRIMTOTA', 'primeotta');
    const amount = this.toNumber(
      fromDetail ?? this.pick(policy, 'PRIMTOTA', 'total', 'primeNette', 'primeotta', 'PRIMTOTA_LDE'),
    );
    return this.formatAmount(amount);
  }

  statusTone(label: string): 'ok' | 'warn' | 'danger' | 'neutral' {
    const value = label.toLowerCase();
    if (value.includes('actif') || value.includes('en cours') || value.includes('valid') || value === '2' || value === '3') {
      return 'ok';
    }
    if (value.includes('suspend') || value.includes('attent') || value.includes('provis') || value === '0' || value === '1') {
      return 'warn';
    }
    if (value.includes('résili') || value.includes('resili') || value.includes('annul') || value.includes('expir') || value === '-1') {
      return 'danger';
    }
    return 'neutral';
  }

  sinisterPoliceNumber(sinister: ApiSinister): string {
    return String(this.pick(sinister, 'policeNumber', 'NUMEPOLI', 'numepoli', 'policyNumber') || '').trim();
  }

  sinisterStatusLabel(status: string | number | null | undefined): string {
    switch (String(status ?? '')) {
      case '0':
        return 'Ouverture';
      case '1':
        return 'En examen';
      case '-1':
        return 'Annulé';
      case '2':
        return 'Validé';
      case '-2':
        return 'Sans suite';
      case '3':
        return 'Clôturé';
      default:
        return 'Inconnu';
    }
  }

  sinisterStatusTone(status: string | number | null | undefined): 'ok' | 'warn' | 'danger' | 'neutral' {
    switch (String(status ?? '')) {
      case '0':
      case '1':
        return 'warn';
      case '2':
      case '3':
        return 'ok';
      case '-1':
        return 'danger';
      default:
        return 'neutral';
    }
  }

  devisAmountValue(devis: ApiDevisProject): number | null {
    const quittance = (devis.quittance || {}) as Record<string, unknown>;
    return this.toNumber(
      this.pick(
        quittance,
        'primTtc',
        'PRIM_TTC',
        'primttc',
        'primtota',
        'PRIMTOTA',
        'primeotta',
        'PRIMTOTA_LDE',
        'primtotaLde',
        'primeottaLde',
      ),
    );
  }

  devisAmount(devis: ApiDevisProject): string {
    return this.formatAmount(this.devisAmountValue(devis));
  }

  devisProductName(devis: ApiDevisProject): string {
    return String(this.pick(devis, 'productName', 'product_name') || 'Devis assurance');
  }

  devisGuarantees(devis: ApiDevisProject): string {
    const items = this.devisGuaranteeList(devis)
      .map((item) => String(this.pick(item, 'libeGara', 'LIBEGARA', 'libegara') || '').trim())
      .filter(Boolean);
    return items.length ? `Garantie : ${this.sentenceCase(items[0])}` : 'Garantie : -';
  }

  formatDate(value: string | null | undefined): string {
    return this.displayApiDate(value);
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    const raw = String(value).trim();
    if (!raw || raw === '-') {
      return '-';
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
      return raw;
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return raw;
    }
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatAmount(value: number | null): string {
    if (value == null) {
      return '-';
    }
    const formatted = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(value);
    return this.currency ? `${formatted} ${this.currency}` : formatted;
  }

  sentenceCase(value: string): string {
    const text = (value || '').trim();
    if (!text) {
      return '';
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
  }

  inputValue(event: Event): string {
    return (event.target as HTMLInputElement | null)?.value ?? '';
  }

  confirmDeleteDevis(devis: ApiDevisProject, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!devis.id || this.deletingDevis) {
      return;
    }
    if (!window.confirm('Supprimer le devis ?')) {
      return;
    }

    this.deletingDevis = true;
    this.myspaceApi
      .deleteDevis(this.phoneNumber, String(devis.id))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deletingDevis = false;
          this.showToast('Devis supprimé avec succès');
          this.closeDetail();
          this.loadDevis();
        },
        error: () => {
          this.deletingDevis = false;
          this.showToast('Échec de la suppression du devis');
        },
      });
  }

  private loadAll(): void {
    this.loadPolicies();
    this.loadDevis();
    this.loadSinisters();
  }

  private loadPolicies(): void {
    if (!this.policyPhoneNumber) {
      this.policiesLoading = false;
      this.policies = [];
      return;
    }

    this.policiesLoading = true;
    this.policiesError = '';
    this.myspaceApi
      .listPolicies(this.policyPhoneNumber)
      .pipe(
        catchError((err) => {
          this.policiesError = extractApiErrorMessage(err, 'Impossible de charger les polices.');
          this.showErrorModal(this.policiesError, () => this.loadPolicies());
          return of([] as ApiPolicy[]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((items) => {
        this.policies = items;
        this.policiesLoading = false;
      });
  }

  private loadDevis(): void {
    if (!this.phoneNumber) {
      this.devisLoading = false;
      this.devisGroups = [];
      return;
    }

    this.devisLoading = true;
    this.devisError = '';
    this.myspaceApi
      .listDevis(this.phoneNumber)
      .pipe(
        catchError((err) => {
          this.devisError = extractApiErrorMessage(err, 'Impossible de charger les devis.');
          this.showErrorModal(this.devisError, () => this.loadDevis());
          return of({} as ApiDevisGroups);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((groups) => {
        this.devisGroups = Object.entries(groups)
          .map(([name, items]) => ({ name, items: items || [] }))
          .filter((group) => group.items.length);
        this.devisLoading = false;
      });
  }

  private loadSinisters(): void {
    if (!this.userId) {
      this.sinistersLoading = false;
      this.sinisterGroups = [];
      return;
    }

    this.sinistersLoading = true;
    this.sinistersError = '';
    this.myspaceApi
      .listSinisters(this.userId)
      .pipe(
        catchError((err) => {
          this.sinistersError = extractApiErrorMessage(err, 'Impossible de charger les prestations.');
          this.showErrorModal(this.sinistersError, () => this.loadSinisters());
          return of({} as ApiSinisterGroups);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((groups) => {
        this.sinisterGroups = Object.entries(groups)
          .map(([name, items]) => ({ name, items: items || [] }))
          .filter((group) => group.items.length);
        this.sinistersLoading = false;
      });
  }

  private toPolicyDetailDto(policy: ApiPolicy): Record<string, string | number> {
    const dto: Record<string, string | number> = {
      codeinte: this.policyValue(policy, 'CODEINTE', 'codeint', 'codeinte'),
      numepoli: this.policyValue(policy, 'NUMEPOLI', 'numpoli', 'numepoli'),
      numeave: this.toNumber(this.pick(policy, 'NUMEAVEN', 'numeave', 'numaven')) ?? 0,
    };
    const idencate = this.policyValue(policy, 'IDENCATE', 'idencate', 'orsyIdentCategory', 'orsyidentcategory');
    if (idencate) {
      dto['idencate'] = /^\d+$/.test(idencate) ? Number(idencate) : idencate;
    }
    return dto;
  }

  private buildPolicySections(policy: ApiPolicy, detail: ApiPolicyDetail | null): AccordionSectionView[] {
    const sections = detail?.policyItemsSections || [];
    if (sections.length) {
      return sections.map((section) => this.mapPolicySection(section)).filter((item) => !this.isEmptySection(item));
    }
    return [
      {
        title: 'Infos générales',
        kind: 'rows',
        rows: [
          { label: 'Assuré', value: this.policyAssured(policy) },
          { label: "Date d'effet", value: this.policyEffectDate(policy) },
          { label: "Date d'échéance", value: this.policyEndDate(policy) },
          { label: 'Prime totale', value: this.policyPrime(policy) },
        ],
      },
    ];
  }

  private buildDevisSections(devis: ApiDevisProject): AccordionSectionView[] {
    const sections: AccordionSectionView[] = [];
    const genInfos = this.asRecordList(devis['genInfos']);
    const insured = this.asRecordList(devis['insured']);
    const beneficiaries = this.asRecordList(devis['beneficiaries']);
    const guarantees = this.devisGuaranteeList(devis)
      .map((item) => this.sentenceCase(String(this.pick(item, 'libeGara', 'LIBEGARA', 'libegara') || '').trim()))
      .filter(Boolean);

    if (genInfos.length) {
      sections.push({
        title: 'Infos générales',
        kind: 'people',
        people: genInfos.map((item, index) => ({
          title: `Infos ${index + 1}`,
          rows: this.mapRecordRows(item),
        })),
      });
    }
    if (guarantees.length) {
      sections.push({ title: `Mes garanties (${guarantees.length})`, kind: 'guarantees', guarantees });
    }
    if (insured.length) {
      sections.push({
        title: `Les assurés (${insured.length})`,
        kind: 'people',
        people: insured.map((item, index) => ({ title: `Assuré ${index + 1}`, rows: this.mapRecordRows(item) })),
      });
    }
    if (beneficiaries.length) {
      sections.push({
        title: `Les bénéficiaires (${beneficiaries.length})`,
        kind: 'people',
        people: beneficiaries.map((item, index) => ({
          title: `Bénéficiaire ${index + 1}`,
          rows: this.mapRecordRows(item),
        })),
      });
    }
    return sections.filter((item) => !this.isEmptySection(item));
  }

  private buildSinisterSections(sinister: ApiSinister): AccordionSectionView[] {
    const detail = (sinister.detail || {}) as Record<string, unknown>;
    const controls = sinister.controls || [];
    const generalRows: DetailRow[] = [
      { label: 'Date de création', value: this.formatDateTime(sinister.createdAt) },
      { label: 'Numéro de police', value: String(sinister.policeNumber || '-') },
      ...controls.map((control) => ({
        label: this.sentenceCase(String(control.label || control.name || 'Champ')),
        value: String(detail[String(control.name || '')] ?? '-'),
      })),
    ];
    return [
      { title: 'Infos générales', kind: 'rows', rows: generalRows },
      { title: 'Sorts', kind: 'events', events: this.mapEvents(sinister.stories) },
      { title: 'Événements', kind: 'events', events: this.mapEvents(sinister.events) },
      { title: 'Règlements', kind: 'events', events: this.mapEvents(sinister.payments) },
    ];
  }

  private resolveDevisProduct(devis: ApiDevisProject): void {
    this.devisProduct = null;
    this.productApi
      .list()
      .pipe(catchError(() => of([] as ApiProductCatalogItem[])), takeUntil(this.destroy$))
      .subscribe((types) => {
        this.devisProduct = this.findDevisProduct(types, devis);
      });
  }

  private findDevisProduct(types: ApiProductCatalogItem[], devis: ApiDevisProject): ApiNestedProduct | null {
    const risk = this.asRecord(devis['risk']);
    const payload = this.asRecord(risk['payload']);
    const code = String(devis['productCode'] || '').trim();
    const cat = String(payload['orsysIdentCategory'] || devis['orsysIdentCategory'] || '').trim();
    let byBoth: ApiNestedProduct | null = null;
    let byCode: ApiNestedProduct | null = null;
    let byCat: ApiNestedProduct | null = null;

    types.forEach((type) => {
      (type.products || []).forEach((product) => {
        const matchesCode = Boolean(code) && (product.orsysCodeCategory === code || product.code === code);
        const matchesCat = Boolean(cat) && product.orsyIdentCategory === cat;
        if (matchesCode && matchesCat) {
          byBoth ??= product;
        }
        if (matchesCode) {
          byCode ??= product;
        }
        if (matchesCat) {
          byCat ??= product;
        }
      });
    });
    return byBoth || byCode || byCat;
  }

  private resetDetailExtras(): void {
    this.policyDetail = null;
    this.policyDetailLoading = false;
    this.policyDetailError = '';
    this.policyAccordionSections = [];
    this.devisAccordionSections = [];
    this.sinisterAccordionSections = [];
    this.cpDownloading = false;
    this.atDownloading = false;
    this.showDevisPayment = false;
    this.devisProduct = null;
    this.validatingDevisPayment = false;
    this.policyProduct = null;
    this.resetPolicyActionView();
  }

  private mapPolicySection(section: ApiPolicyDetailSection): AccordionSectionView {
    const title = this.sentenceCase(section.title || 'Section') || 'Section';
    const key = (section.title || '').toLowerCase();
    const items = section.items || [];

    if (key.includes('bénéficiaire') || key.includes('beneficiaire')) {
      return {
        title,
        kind: 'people',
        people: items.map((item, index) => ({
          title: String(item.value || item.name || `Bénéficiaire ${index + 1}`),
          rows: [
            { label: 'Nom', value: String(item.value || item.name || '-') },
            { label: 'Lien', value: this.sentenceCase(item.lienParente || '') || '-' },
            { label: 'Part', value: String(item.partBeneficiaire || '-') },
          ],
        })),
      };
    }

    if (key.includes('garantie')) {
      return {
        title,
        kind: 'guarantees',
        guarantees: items
          .map((item) => this.sentenceCase(String(item.label || item.natugara || item.value || '').trim()))
          .filter(Boolean),
      };
    }

    return {
      title,
      kind: 'rows',
      rows: items.map((item) => this.mapPolicyItemRow(item)),
    };
  }

  private mapPolicyItemRow(item: ApiPolicyDetailItem): DetailRow {
    const raw = item.value ?? '-';
    const value =
      item.type === 'current' ? this.formatAmount(this.toNumber(raw)) : String(raw || '-');
    return { label: String(item.label || 'Champ'), value };
  }

  private isEmptySection(section: AccordionSectionView): boolean {
    if (section.kind === 'rows') {
      return !(section.rows || []).length;
    }
    if (section.kind === 'people') {
      return !(section.people || []).some((person) => person.rows.length);
    }
    if (section.kind === 'guarantees') {
      return !(section.guarantees || []).length;
    }
    return !(section.events || []).length;
  }

  private mapEvents(items: ApiSinisterEvent[] | null | undefined): DetailRow[] {
    return (items || []).map((item) => ({
      label: this.sentenceCase(String(item.designation || 'Élément')),
      value: this.formatDateTime(item.createdAt),
    }));
  }

  private mapRecordRows(record: Record<string, unknown>): DetailRow[] {
    return Object.entries(record)
      .filter(([key, value]) => !['id', 'index'].includes(key) && this.isPrimitive(value))
      .map(([key, value]) => ({ label: String(key), value: String(value) }));
  }

  private asRecordList(value: unknown): Record<string, unknown>[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object' && !Array.isArray(item));
  }

  private asRecord(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    return {};
  }

  private isPrimitive(value: unknown): boolean {
    return value != null && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean');
  }

  private policyCategoryKey(policy: ApiPolicy): string {
    return this.policyValue(policy, 'IDENCATE', 'idencate', 'orsyIdentCategory', 'orsyidentcategory', 'CODECATE', 'codecate');
  }

  private devisGuaranteeList(devis: ApiDevisProject): ApiDevisGuarantee[] {
    const raw = devis.guaranties ?? devis['garanties'] ?? devis['guarantees'];
    return Array.isArray(raw) ? (raw as ApiDevisGuarantee[]) : [];
  }

  private displayApiDate(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    const raw = String(value).trim();
    if (!raw || raw === '-') {
      return '-';
    }
    if (/^\d{2}\/\d{2}\/\d{4}/.test(raw)) {
      return raw.length > 10 ? raw.slice(0, 10) : raw;
    }
    const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      return `${iso[3]}/${iso[2]}/${iso[1]}`;
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return raw;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${date.getFullYear()}`;
  }

  private pick(record: Record<string, unknown> | null | undefined, ...keys: string[]): unknown {
    if (!record) {
      return undefined;
    }
    for (const key of keys) {
      const value = record[key];
      if (value != null && String(value).trim() !== '') {
        return value;
      }
    }
    const lowerMap = new Map(Object.keys(record).map((key) => [key.toLowerCase(), record[key]]));
    for (const key of keys) {
      const value = lowerMap.get(key.toLowerCase());
      if (value != null && String(value).trim() !== '') {
        return value;
      }
    }
    return undefined;
  }

  private policyValue(policy: ApiPolicy, ...keys: string[]): string {
    const value = this.pick(policy, ...keys);
    return value == null ? '' : String(value).trim();
  }

  private showErrorModal(message: string, retry?: () => void): void {
    const text = (message || '').trim();
    if (!text) {
      return;
    }
    this.errorModalMessage = text;
    this.errorModalCanRetry = Boolean(retry);
    this.errorModalRetry = retry ?? null;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value !== 'string' || !value.trim()) {
      return null;
    }
    const currency = (this.currency || '').trim();
    let raw = value.trim();
    if (currency) {
      raw = raw.replace(new RegExp(currency.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig'), '');
    }
    raw = raw.replace(/[^\d,.-]/g, '').replace(/\s/g, '').replace(',', '.');
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private showToast(message: string): void {
    this.toastMessage = message;
    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
    }
    this.toastTimer = setTimeout(() => {
      this.toastMessage = '';
      this.toastTimer = null;
    }, 3000);
  }
}
